import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import VideoCard from "@/components/base/VideoCard";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useFavorites } from "@/hooks/useFavorites";
import { formatViews, getRelatedVideos, getVideoById, typeLabel } from "@/lib/videos";
import PlayerMock from "@/pages/detail/components/PlayerMock";
import MetaPanel from "@/pages/detail/components/MetaPanel";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const video = getVideoById(id);
  const { isFavorite, toggleFavorite } = useFavorites();

  usePageSeo({
    title: video ? `激情视频 - ${video.title}详情介绍` : "激情视频 - 作品详情",
    description: video
      ? `${video.title}（${video.year}·${video.region}）${video.summary} ${typeLabel(video.type)}，时长${video.duration}分钟，导演${video.director}。`
      : "激情视频作品详情页，查看作品简介、元信息与相关推荐。",
    keywords: video
      ? `${video.title},${video.category},${video.region},${video.tags.join(",")},激情视频详情`
      : "激情视频,作品详情",
  });

  if (!video) {
    return (
      <SiteLayout>
        <div className="px-4 md:px-8 lg:px-12 py-20 text-center">
          <span className="w-16 h-16 mx-auto rounded-full bg-background-200 flex items-center justify-center text-foreground-500">
            <i className="ri-file-unknow-line text-2xl" />
          </span>
          <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-50">作品不存在或已下架</h1>
          <p className="mt-2 text-sm text-foreground-500">链接中的作品 ID 没有匹配到内容，可能已被移除。</p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 mt-6 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedVideos(video, 6);
  const favorite = isFavorite(video.id);

  return (
    <SiteLayout>
      <div className="px-4 md:px-8 lg:px-12 pt-5 md:pt-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: video.type === "movie" ? "激情电影" : "激情短视频", to: video.type === "movie" ? "/category/movie" : "/category/short" },
            { label: video.title },
          ]}
        />
      </div>

      <section className="px-4 md:px-8 lg:px-12 mt-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6">
          <div className="lg:col-span-7 xl:col-span-8">
            <PlayerMock video={video} />

            <div className="mt-5 rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6">
              <div className="flex items-center gap-2.5 mb-3">
                <span className="h-[2px] w-6 bg-primary-500" />
                <span className="text-[11px] tracking-[0.24em] text-accent-400 font-label uppercase">Synopsis</span>
              </div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50">剧情简介</h2>
              <p className="mt-3 text-sm leading-[1.9] text-foreground-400">{video.summary}</p>
              <p className="mt-3 text-sm leading-[1.9] text-foreground-400">{video.description}</p>

              <div className="mt-5 pt-4 border-t border-background-300/40 flex items-center gap-4 flex-wrap text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line text-foreground-600" />
                  {formatViews(video.views)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-line text-foreground-600" />
                  评分 {video.rating.toFixed(1)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line text-foreground-600" />
                  收录于 {video.publishDate}
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 xl:col-span-4">
            <MetaPanel video={video} isFavorite={favorite} onToggleFavorite={() => toggleFavorite(video.id)} />
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 lg:px-12 mt-10 md:mt-14">
        <div className="flex items-end justify-between gap-4 mb-5">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="h-[2px] w-6 bg-accent-500" />
              <span className="text-[11px] tracking-[0.24em] text-accent-400 font-label uppercase">You May Like</span>
            </div>
            <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-50">相关推荐</h2>
            <p className="mt-1.5 text-xs text-foreground-500">基于「{video.category}」分类标签为你推荐</p>
          </div>
          <Link
            to={video.type === "movie" ? "/category/movie" : "/category/short"}
            className="hidden sm:flex items-center gap-1 h-9 px-3.5 rounded-md border border-background-300/70 text-xs text-foreground-300 hover:border-primary-400 hover:text-foreground-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            更多同类作品
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {related.map((item) => (
            <VideoCard key={item.id} video={item} />
          ))}
        </div>
      </section>

      <section className="px-4 md:px-8 lg:px-12 mt-10">
        <div className="rounded-lg bg-background-200/40 border border-background-200/70 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-foreground-500 leading-relaxed">
            <i className="ri-information-line mr-1" />
            本站所有作品信息与封面均为演示数据，视频区域不加载真实视频流。
          </p>
          <Link
            to="/about"
            className="h-9 px-4 rounded-md bg-secondary-800 text-secondary-100 text-xs hover:bg-secondary-700 transition-colors whitespace-nowrap flex items-center cursor-pointer"
          >
            了解版权声明
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}